import React, { useState, useEffect } from 'react';
import { cn } from '../../lib/utils';
import { Menu, X } from 'lucide-react';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '#features', label: 'Features' },
    { href: '#architecture', label: 'How It Works' },
    { href: '#methodology', label: 'Methodology' },
    { href: '#compliance', label: 'Compliance' },
    { href: '#platforms', label: 'Platforms' },
    { href: '#faq', label: 'FAQ' },
  ];

  return (
    <nav
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-500',
        isScrolled
          ? 'glass border-b border-white/5 shadow-lg shadow-black/10'
          : 'bg-transparent'
      )}
      style={{ transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)' }}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo - Premium Brand Treatment */}
          <a href="#" className="flex items-center gap-4 group relative">
            {/* Animated background container */}
            <div className="absolute -inset-3 rounded-2xl bg-gradient-to-r from-blue-500/0 via-purple-500/0 to-cyan-500/0 group-hover:from-blue-500/10 group-hover:via-purple-500/10 group-hover:to-cyan-500/10 transition-all duration-500 group-hover:backdrop-blur-sm" />

            {/* Logo with orbital rings effect */}
            <div className="relative">
              {/* Outer rotating ring */}
              <div className="absolute -inset-2 rounded-full border border-cyan-500/0 group-hover:border-cyan-500/30 group-hover:animate-spin transition-all duration-500" style={{ animationDuration: '8s' }} />
              {/* Inner pulsing glow */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500 via-purple-500 to-cyan-500 rounded-xl blur-xl opacity-30 group-hover:opacity-70 group-hover:scale-150 transition-all duration-500" />
              {/* Sparkle effect on hover */}
              <div className="absolute -inset-1 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-pulse rounded-xl transition-opacity duration-300" />
              <img
                src="/astra-logo.png"
                alt="Astra Express Lane HFT"
                className="w-12 h-12 sm:w-14 sm:h-14 object-contain group-hover:scale-110 transition-all duration-300 relative z-10 drop-shadow-[0_0_10px_rgba(34,211,238,0.5)]"
              />
            </div>

            {/* Text with special treatment - centered alignment */}
            <div className="flex flex-col items-center relative z-10 notranslate">
              <div className="relative">
                <span className="text-xl sm:text-2xl font-black text-gradient leading-tight tracking-tight">ASTRA</span>
                {/* Underline accent that animates on hover */}
                <div className="absolute -bottom-0.5 left-0 w-0 h-0.5 bg-gradient-to-r from-blue-500 via-purple-500 to-cyan-500 group-hover:w-full transition-all duration-500" />
              </div>
              <div className="flex items-center gap-1 mt-0.5">
                {/* Lightning bolt icon - always visible */}
                <svg className="w-2.5 h-2.5 text-yellow-400 group-hover:scale-125 transition-transform duration-300" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                </svg>
                <span className="text-[10px] sm:text-xs font-bold text-cyan-400/80 group-hover:text-cyan-300 leading-tight tracking-[0.2em] transition-colors duration-300">EXPRESS LANE HFT</span>
              </div>
            </div>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-sm font-medium text-muted-foreground hover:text-white transition-colors"
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href="#demo"
              className="text-sm font-medium text-muted-foreground hover:text-white transition-colors"
            >
              Benchmarks
            </a>
            <a
              href="#contact"
              className="px-4 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-sm font-medium rounded-lg hover:opacity-90 transition-opacity"
            >
              Contact Us
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-muted-foreground hover:text-white"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-border">
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="text-sm font-medium text-muted-foreground hover:text-white transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.label}
                </a>
              ))}
              <a
                href="#contact"
                className="px-4 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-sm font-medium rounded-lg text-center hover:opacity-90 transition-opacity"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Contact Us
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
